import React, { useState } from 'react';
import { Calendar, Clock, MapPin, User, Mail, Phone as PhoneIcon, Car } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { Card } from './ui/card';
import { toast } from 'sonner';
import { services } from '../mockData';
import { bookingAPI } from '../services/api';

export const BookingForm = () => {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone: '',
    service: '',
    vehicle: '',
    date: '',
    time: '',
    address: '',
    notes: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Check if user is a member
      const memberData = JSON.parse(localStorage.getItem('memberData') || 'null');
      
      const bookingData = {
        member_email: memberData?.email || null,
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        service: formData.service,
        vehicle: formData.vehicle,
        preferred_date: formData.date,
        preferred_time: formData.time,
        address: formData.address,
        notes: formData.notes
      };
      
      const response = await bookingAPI.create(bookingData);
      
      toast.success('Booking Request Submitted!', {
        description: `Booking #${response.booking_number} - We'll contact you shortly to confirm your appointment.`
      });
      
      // Reset form
      setFormData({
        name: '',
        email: '',
        phone: '',
        service: '',
        vehicle: '',
        date: '',
        time: '',
        address: '',
        notes: ''
      });
    } catch (error) {
      console.error('Error creating booking:', error);
      toast.error('Failed to submit booking. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (field, value) => {
    setFormData({ ...formData, [field]: value });
  };

  const timeSlots = [
    '7:00 AM', '8:00 AM', '9:00 AM', '10:00 AM', '11:00 AM',
    '12:00 PM', '1:00 PM', '2:00 PM', '3:00 PM', '4:00 PM',
    '5:00 PM', '6:00 PM', '7:00 PM'
  ];

  return (
    <section id="booking" className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
      <div className="max-w-4xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-12">
          <h2 className="text-4xl lg:text-5xl font-extrabold text-gray-900 mb-4">
            Book Your Service
          </h2>
          <p className="text-xl text-gray-600">
            Schedule your appointment and we'll come to you
          </p>
        </div>

        <Card className="p-8 shadow-xl">
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Personal Information */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="name" className="flex items-center mb-2">
                  <User className="w-4 h-4 mr-2 text-red-600" />
                  Full Name *
                </Label>
                <Input
                  id="name"
                  required
                  value={formData.name}
                  onChange={(e) => handleChange('name', e.target.value)}
                  placeholder="John Doe"
                  className="border-gray-300 focus:border-red-500 focus:ring-red-500"
                />
              </div>
              <div>
                <Label htmlFor="phone" className="flex items-center mb-2">
                  <PhoneIcon className="w-4 h-4 mr-2 text-red-600" />
                  Phone Number *
                </Label>
                <Input
                  id="phone"
                  type="tel"
                  required
                  value={formData.phone}
                  onChange={(e) => handleChange('phone', e.target.value)}
                  placeholder="(555) 123-4567"
                  className="border-gray-300 focus:border-red-500 focus:ring-red-500"
                />
              </div>
            </div>

            <div>
              <Label htmlFor="email" className="flex items-center mb-2">
                <Mail className="w-4 h-4 mr-2 text-red-600" />
                Email Address *
              </Label>
              <Input
                id="email"
                type="email"
                required
                value={formData.email}
                onChange={(e) => handleChange('email', e.target.value)}
                placeholder="john@example.com"
                className="border-gray-300 focus:border-red-500 focus:ring-red-500"
              />
            </div>

            {/* Service Details */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="service" className="mb-2 block">
                  Select Service *
                </Label>
                <Select required value={formData.service} onValueChange={(value) => handleChange('service', value)}>
                  <SelectTrigger className="border-gray-300 focus:border-red-500 focus:ring-red-500">
                    <SelectValue placeholder="Choose a service" />
                  </SelectTrigger>
                  <SelectContent>
                    {services.map((service) => (
                      <SelectItem key={service.id} value={service.name}>
                        {service.name}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label htmlFor="vehicle" className="flex items-center mb-2">
                  <Car className="w-4 h-4 mr-2 text-red-600" />
                  Vehicle Info *
                </Label>
                <Input
                  id="vehicle"
                  required
                  value={formData.vehicle}
                  onChange={(e) => handleChange('vehicle', e.target.value)}
                  placeholder="2020 Toyota Camry"
                  className="border-gray-300 focus:border-red-500 focus:ring-red-500"
                />
              </div>
            </div>

            {/* Date and Time */}
            <div className="grid md:grid-cols-2 gap-6">
              <div>
                <Label htmlFor="date" className="flex items-center mb-2">
                  <Calendar className="w-4 h-4 mr-2 text-red-600" />
                  Preferred Date *
                </Label>
                <Input
                  id="date"
                  type="date"
                  required
                  value={formData.date}
                  onChange={(e) => handleChange('date', e.target.value)}
                  min={new Date().toISOString().split('T')[0]}
                  className="border-gray-300 focus:border-red-500 focus:ring-red-500"
                />
              </div>
              <div>
                <Label htmlFor="time" className="flex items-center mb-2">
                  <Clock className="w-4 h-4 mr-2 text-red-600" />
                  Preferred Time *
                </Label>
                <Select required value={formData.time} onValueChange={(value) => handleChange('time', value)}>
                  <SelectTrigger className="border-gray-300 focus:border-red-500 focus:ring-red-500">
                    <SelectValue placeholder="Select time" />
                  </SelectTrigger>
                  <SelectContent>
                    {timeSlots.map((slot) => (
                      <SelectItem key={slot} value={slot}>
                        {slot}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Location */}
            <div>
              <Label htmlFor="address" className="flex items-center mb-2">
                <MapPin className="w-4 h-4 mr-2 text-red-600" />
                Service Address *
              </Label>
              <Input
                id="address"
                required
                value={formData.address}
                onChange={(e) => handleChange('address', e.target.value)}
                placeholder="123 Main St, Dallas, TX 75001"
                className="border-gray-300 focus:border-red-500 focus:ring-red-500"
              />
            </div>

            {/* Additional Notes */}
            <div>
              <Label htmlFor="notes" className="mb-2 block">
                Additional Notes (Optional)
              </Label>
              <Textarea
                id="notes"
                value={formData.notes}
                onChange={(e) => handleChange('notes', e.target.value)}
                placeholder="Any special requirements or additional information..."
                rows={4}
                className="border-gray-300 focus:border-red-500 focus:ring-red-500"
              />
            </div>

            {/* Submit Button */}
            <Button
              type="submit"
              size="lg"
              disabled={isSubmitting}
              className="w-full bg-red-600 hover:bg-red-700 text-white text-lg py-6 shadow-lg hover:shadow-xl transition-all"
            >
              {isSubmitting ? 'Submitting...' : 'Submit Booking Request'}
            </Button>

            <p className="text-sm text-gray-500 text-center">
              * Required fields. We'll contact you within 2 hours to confirm your appointment.
            </p>
          </form>
        </Card>
      </div>
    </section>
  );
};